import { CustomerSearchRequestQuery, CustomerSearchResponse } from '@lio/api-types';
import { customerQueryKey } from '@services/customer/keys';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { omitBy, isNil } from 'lodash';

/**
 * Paginated (infinite) customer search, modeled on `useSearchPaginatedBrokerages`.
 * Used by typeahead pickers that load more results as the user scrolls the list.
 */
export const useSearchPaginatedCustomers = (params: CustomerSearchRequestQuery, enabled = true) => {
  const client = useClient();
  const baseParams = omitBy(
    {
      name: params.name ?? '',
      // Stringify here so the values are genuinely strings, not a cast that masks the
      // number/string mismatch (URLSearchParams would otherwise stringify at runtime).
      pageLimit: String(params.pageLimit ?? 25),
      sort: params.sort ?? 'name',
    },
    isNil,
  ) as Record<string, string>;

  const result = useInfiniteQuery<CustomerSearchResponse, Error>({
    queryKey: [...customerQueryKey.searchCustomers({ ...params }), 'infinite'],
    queryFn: ({ pageParam }) => {
      const queryString = new URLSearchParams({ ...baseParams, pageOffset: String(pageParam) });
      return client.get(`customer?${queryString}`).json<CustomerSearchResponse>();
    },
    initialPageParam: 0,
    enabled,
    staleTime: 2000,
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.pagination?.pageLimit || !lastPage.pagination.total) return undefined;
      const { total, pageLimit } = lastPage.pagination;
      const nextPageOffsetValue = allPages.length * pageLimit;
      if (nextPageOffsetValue >= total) return undefined;
      return nextPageOffsetValue;
    },
  });

  const customers = result.data?.pages.flatMap((page) => page.items) ?? [];

  return { ...result, customers };
};
